import { TechemTheme } from '@techem/techem-theme';
import { Button } from '@techem/ui-components';
import minusIcon from 'presentation/assets/icons/png/3x/icn-circle-minus@3x.png';
import plusIcon from 'presentation/assets/icons/png/3x/icn-circle-plus@3x.png';
import { useStyletron } from 'baseui';
import { FormControl as FormControlBaseWeb } from 'baseui/form-control';
import { SIZE } from 'baseui/input';
import Icon, { ICONSIZE } from 'presentation/components/Icon';
import Input from 'presentation/components/Input';
import RedLinkButtonWithArrow from 'presentation/components/RedLinkButtonWithArrow';
import TitleWithPrevPageLink from 'presentation/components/TitleWithPrevPageLink';
import { WizardSteps, WizardStepsContainer } from 'presentation/components/WizardSteps';
import React, { useEffect } from 'react';
import { Col, Container, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { setNumberOfApartments } from 'frameworks_drivers/redux/reducers/onboardingInformation/onboardingInformationSlice';
import { paths } from 'frameworks_drivers/routes';
import store, { useAppDispatch } from 'store';
import useAutoFocus from 'tools/hooks/useAutoFocus';
import { TrackerUtil } from 'tracking/TrackerUtil';
import { useApartmentCount } from '../../../../tools/hooks/useApartmentCount';
import { ApartmentCountExceededModal } from './ApartmentCountExceededModal';
import MultipleHousesModal from './MultipleHousesModal';
import { useTrackGoogleEvents } from '../../../../interface_adapters/hooks/GoogleTagContext';
import { useApartmentCountLimits } from '../../../../interface_adapters/hooks/ApartmentCountLimitsContext';
import { DataProcessingAgreementApiService } from '../../../../interface_adapters/services/dataProcessingAgreement/DataProcessingAgreementApiService';
import { setDataProcessingAgreement } from '../../../../frameworks_drivers/redux/reducers/miscellaneous/miscSlice';

export const ApartmentCountPage = () => {
  const [isModalOpen, setIsModalOpen] = React.useState<boolean>(false);
  const { trackEnterOnboarding } = useTrackGoogleEvents();

  const limits = useApartmentCountLimits();

  const dispatch = useAppDispatch();

  const navigate = useNavigate();
  const apartmentCountInputRef = useAutoFocus();
  const [css] = useStyletron();
  const { t } = useTranslation('translation');

  const {
    apartmentCount,
    isApartmentCountExceededModalOpen,
    setApartmentCountExceededModalOpen,
    increaseCount,
    decreaseCount,
    handleCountChange,
    handleBlur
  } = useApartmentCount(store.getState().onboardingInfo.numberOfApartments ?? limits.getMin());

  useEffect(() => {
    trackEnterOnboarding();
    const dpaService = new DataProcessingAgreementApiService();
    dpaService.loadLatestVersion().then((dataProcessingAgreement) => {
      console.debug('Used data processing agreement: ', dataProcessingAgreement);

      if (dataProcessingAgreement) dispatch(setDataProcessingAgreement(dataProcessingAgreement));
    });
  }, [dispatch, trackEnterOnboarding]);

  const handlePrevBtnClicked = () => {
    TrackerUtil.trackPrevBtnClick();
    navigate(paths.LandingPage);
  };

  const handleSubmit = (e: React.FormEvent) => {
    if (canBeSubmitted()) {
      store.dispatch(setNumberOfApartments(apartmentCount));

      TrackerUtil.trackNextBtnClick();
      navigate(paths.ThermalMetering);

      e.preventDefault();
    }
  };

  const canBeSubmitted = (): boolean => {
    return apartmentCount >= limits.getMin() && apartmentCount <= limits.getMax();
  };

  return (
    <Container className="pb-4 mb-5">
      <WizardStepsContainer>
        <WizardSteps currentStep={1} totalSteps={6} />
      </WizardStepsContainer>
      <TitleWithPrevPageLink
        onPrevClick={handlePrevBtnClicked}
        title={t('pages.apartmentCount.title')}
      />

      <form onSubmit={handleSubmit}>
        <Row className="justify-content-center mt-5">
          <Col xs="auto" className="text-center mt-2">
            <Icon
              iconSrc={minusIcon}
              handleClick={decreaseCount}
              size={ICONSIZE.large}
              disabled={apartmentCount <= limits.getMin()}
              additionalTestId="decrease"
              alt="decrease icon"
            />
          </Col>
          <Col xs={4} sm={4} md={3} lg={2} className="text-center">
            <FormControlBaseWeb
              label={() => <span>{t('pages.apartmentCount.apartmentLabel')}</span>}
              caption={t('pages.apartmentCount.apartmentLabel')}
              overrides={{
                LabelContainer: {
                  props: {
                    className: 'visually-hidden'
                  }
                },
                Caption: {
                  style: () => {
                    return {
                      fontSize: '0.875rem',
                      textAlign: 'center',
                      color: TechemTheme.colors.contentSecondary
                    };
                  }
                }
              }}
            >
              <Input
                id="apartment-count-input"
                name="apartmentCount"
                type="number"
                value={apartmentCount}
                onChange={({ currentTarget: { value } }) => handleCountChange(parseInt(value))}
                onBlur={({ currentTarget: { value } }) => handleBlur(parseInt(value))}
                size={SIZE.default}
                inputStyleOverrides={{
                  textAlign: 'center',
                  fontSize: '24px',
                  fontWeight: 'normal'
                }}
                additionalTestId="numberOfApartments"
                inputRef={apartmentCountInputRef}
                aria-label={t('pages.apartmentCount.apartmentLabel') ?? ''}
              />
            </FormControlBaseWeb>
          </Col>
          <Col xs="auto" className="text-center mt-2">
            <Icon
              iconSrc={plusIcon}
              handleClick={increaseCount}
              size={ICONSIZE.large}
              disabled={apartmentCount >= limits.getMax()}
              additionalTestId="increase"
              alt="increase icon"
            />
          </Col>
        </Row>

        <Row className="justify-content-center py-5">
          <Col className="text-center col-auto">
            <Button
              disabled={!canBeSubmitted()}
              size={SIZE.large}
              type="submit"
              additionalTestId="next"
            >
              {t('nextBtnText')}
            </Button>
          </Col>
        </Row>
      </form>

      <p className="copy text-center">
        {t('pages.apartmentCount.youWantMore')}{' '}
        <span className={css({ whiteSpace: 'nowrap' }) + ' ms-2'}>
          <RedLinkButtonWithArrow
            ariaLabel={'Mehr Informationen zur Ausstattung mit mehr als 20 Wohnungen'}
            handleClick={() => {
              TrackerUtil.trackLinkClick(
                `${TrackerUtil.getPageName()}_inline-button_more-properties`,
                'inline-button_more-properties'
              );
              setIsModalOpen(true);
            }}
            additionalTestId="contact-us-this-way"
          >
            {t('ContactUsThisWay')}
          </RedLinkButtonWithArrow>
        </span>
      </p>

      <p className="copy text-center" style={{ fontSize: '.85em' }}>
        {t('pages.apartmentCount.residentialApartment')}
        <br />
        <small style={{ fontSize: '.8em' }}>
          {t('pages.apartmentCount.residentialApartmentAdditionalInformation')}
        </small>
        <span className={css({ whiteSpace: 'nowrap' }) + ' ms-2'}></span>
      </p>

      <ApartmentCountExceededModal
        isOpen={isApartmentCountExceededModalOpen}
        setOpen={setApartmentCountExceededModalOpen}
      />

      <MultipleHousesModal isOpen={isModalOpen} setOpen={setIsModalOpen} />
    </Container>
  );
};
